import api from "./base_url";

const formatCartItems = (items = []) => {
  return (items || []).map((i) => {
    // In Redux cart, i.qty is the user-selected cart quantity (e.g. 5, 6).
    // i.quantity is the product's packaging/unit attribute from DB (e.g. "1" or "1 sheet").
    // We must prioritize i.qty first, falling back to i.quantity, and ensure integer output.
    const rawQty = i.qty != null ? i.qty : i.quantity;
    const parsedQty = parseInt(rawQty, 10);
    return {
      product: i.product || i.id,
      quantity: !isNaN(parsedQty) && parsedQty > 0 ? parsedQty : 1,
    };
  });
};

/**
 * Apply and validate a promo code against cart items
 * @param {string} code - Promo code
 * @param {Array} items - Cart items in [{ product: id, quantity: qty }] format
 * @param {string} state - Shipping state name (optional)
 * @param {string|number} customerId - Customer ID if applicable (optional)
 * @returns {Promise<object>} - Backend response with discount details
 */
export const applyCouponApi = async (code, items = [], state = "", customerId = null) => {
  const normalizedCode = (code || "").trim().toUpperCase();
  const payload = {
    code: normalizedCode,
    coupon_code: normalizedCode,
    items: formatCartItems(items),
    state: state || "",
    customer_id: customerId || null,
  };

  try {
    const res = await api.post("coupons/apply/", payload);
    return res.data;
  } catch (err) {
    if (err.response?.status === 404) {
      const res = await api.post("payment/coupons/apply/", payload);
      return res.data;
    }
    throw err;
  }
};

/**
 * Fetch list of active promo codes with customer eligibility status
 * Supports optional JWT Auth Token automatically attached via axios interceptors
 * @returns {Promise<Array>} - List of coupons
 */
export const getAvailableCouponsApi = async () => {
  try {
    const res = await api.get("coupons/available/");
    const data = res.data;
    if (Array.isArray(data)) return data;
    return data.data || data.coupons || data.results || [];
  } catch (err) {
    if (err.response?.status === 404) {
      const res = await api.get("payment/coupons/available/");
      const data = res.data;
      if (Array.isArray(data)) return data;
      return data.data || data.coupons || data.results || [];
    }
    throw err;
  }
};

/**
 * Fetch full cart price summary with optional shipping state and coupon code
 * @param {Array} items - Cart items
 * @param {string} state - Shipping state name
 * @param {string} couponCode - Applied coupon code
 * @param {string|number} customerId - Optional customer id
 * @returns {Promise<object>} - Price summary including discount breakdown
 */
export const getCartPriceSummaryApi = async (items = [], state = "", couponCode = "", customerId = null) => {
  const payload = {
    items: formatCartItems(items),
    state: state || "",
    customer_id: customerId || null,
  };
  if (couponCode && couponCode.trim()) {
    payload.coupon_code = couponCode.trim().toUpperCase();
  }

  try {
    const res = await api.post("cart/price-summary/", payload);
    return res.data;
  } catch (err) {
    if (err.response?.status === 404) {
      const res = await api.post("payment/cart/price-summary/", payload);
      return res.data;
    }
    throw err;
  }
};

